import * as React from "react";
import {Window} from "@progress/kendo-react-dialogs";
import {useState} from "react";
import {Button} from "@progress/kendo-react-buttons";
import {DropDownList} from "@progress/kendo-react-dropdowns";
import {TextArea} from "@progress/kendo-react-inputs";
import {Label} from "@progress/kendo-react-labels";
import useCashflowStore from "../../stores/CashflowStore";
import useBankNotificationStore from "../../stores/BankNotificationStore";
import useClientStore from "../../stores/ClientStore";
import useCurrencyStore from "../../stores/CurrencyStore";
import {filterBy} from "@progress/kendo-data-query";
import {useEffect} from "react";

const MatchDialog = (props) => {

    const {notification, onClose} = props;

    const [selectedCashflow, setSelectedCashflow] = useState(null);
    const [note, setNote] = useState('');
    const [filter, setFilter] = useState('');

    const cashflowList = useCashflowStore(state => state.cashflow)
    const matchManually = useBankNotificationStore(state => state.matchManually)
    const isLoading = useBankNotificationStore(state => state.isLoading)
    const clients = useClientStore(state => state.clients)
    const currencies = useCurrencyStore(state => state.data)
    const loadCurrencies = useCurrencyStore(state => state.loadData)

    // Load currencies if not loaded
    useEffect(() => {
        if (!currencies || currencies.length === 0) {
            loadCurrencies()
        }
    }, [])

    // Helper to get client name by ID
    const getClientName = (clientId) => {
        if (!clientId || !clients) return ''
        const client = clients.find(c => c.ID === clientId)
        return client?.Name || ''
    }

    // Helper to get currency code by ID
    const getCurrencyCode = (currencyId) => {
        if (!currencyId || !currencies) return ''
        const currency = currencies.find(c => c.ID === currencyId)
        return currency?.Name || ''
    }

    const formatter = new Intl.NumberFormat('cs-CZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    // Filter cashflow for dropdown - show number, counterparty, text, amount
    const cashflowData = cashflowList.map(cf => {
        const counterPartyName = getClientName(cf.CounterPartyID)
        const text = cf.Text || ''
        const amount = formatter.format(cf.PriceVat || 0)
        const currency = getCurrencyCode(cf.CurrencyID)

        // Format: "VS | Protistrana | Text | Částka"
        let displayText = cf.Number || 'N/A'
        if (counterPartyName) displayText += ` | ${counterPartyName}`
        if (text) displayText += ` | ${text.substring(0, 30)}${text.length > 30 ? '...' : ''}`
        displayText += ` | ${amount} ${currency}`

        return {
            ...cf,
            displayText,
            counterPartyName,
            currencyCode: currency,
            textValue: text
        }
    })

    const filteredData = filter
        ? filterBy(cashflowData, {
            logic: 'or',
            filters: [
                {field: 'Number', operator: 'contains', value: filter, ignoreCase: true},
                {field: 'counterPartyName', operator: 'contains', value: filter, ignoreCase: true},
                {field: 'textValue', operator: 'contains', value: filter, ignoreCase: true},
            ]
        })
        : cashflowData;

    const handleFilterChange = (event) => {
        setFilter(event.filter.value);
    };

    const handleSubmit = async () => {
        if (!selectedCashflow) {
            alert('Vyberte cashflow pro spárování');
            return;
        }

        try {
            await matchManually(notification.ID, selectedCashflow.ID, note);
            onClose();
        } catch (error) {
            alert('Chyba při párování: ' + error.message);
        }
    };

    return (
        <Window
            title="Manuální spárování platby"
            onClose={onClose}
            initialHeight={600}
            initialWidth={650}
            modal={true}
        >
            <div style={{padding: 16}}>
                {/* Notification info */}
                <div style={{marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4}}>
                    <h4 style={{marginTop: 0, marginBottom: 12}}>Bankovní notifikace</h4>
                    <table style={{width: '100%'}}>
                        <tbody>
                        <tr>
                            <td style={{fontWeight: 'bold', width: 150}}>Částka:</td>
                            <td>{formatter.format(notification.Amount)} {notification.Currency}</td>
                        </tr>
                        <tr>
                            <td style={{fontWeight: 'bold'}}>VS:</td>
                            <td>{notification.VariableSymbol || '-'}</td>
                        </tr>
                        <tr>
                            <td style={{fontWeight: 'bold'}}>Protiúčet:</td>
                            <td>{notification.CounterpartyAccount || '-'}</td>
                        </tr>
                        <tr>
                            <td style={{fontWeight: 'bold'}}>Zpráva:</td>
                            <td>{notification.Message || '-'}</td>
                        </tr>
                        <tr>
                            <td style={{fontWeight: 'bold'}}>Datum:</td>
                            <td>{notification.TransactionDate ? new Date(notification.TransactionDate).toLocaleDateString('cs-CZ') : '-'}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                {/* Cashflow selection */}
                <div style={{marginBottom: 16}}>
                    <Label style={{display: 'block', marginBottom: 8, fontWeight: 'bold'}}>
                        Vyberte cashflow pro spárování:
                    </Label>
                    <DropDownList
                        data={filteredData}
                        textField="displayText"
                        dataItemKey="ID"
                        value={selectedCashflow}
                        onChange={(e) => setSelectedCashflow(e.value)}
                        filterable={true}
                        onFilterChange={handleFilterChange}
                        style={{width: '100%'}}
                        placeholder="Vyberte cashflow..."
                    />
                </div>

                {/* Selected cashflow info */}
                {selectedCashflow && (
                    <div style={{marginBottom: 16, padding: 12, backgroundColor: '#e8f5e9', borderRadius: 4}}>
                        <strong>Vybraný cashflow:</strong>
                        <table style={{width: '100%', marginTop: 8}}>
                            <tbody>
                            <tr>
                                <td style={{fontWeight: 'bold', width: 120}}>Číslo (VS):</td>
                                <td>{selectedCashflow.Number}</td>
                            </tr>
                            <tr>
                                <td style={{fontWeight: 'bold'}}>Protistrana:</td>
                                <td>{getClientName(selectedCashflow.CounterPartyID) || '-'}</td>
                            </tr>
                            <tr>
                                <td style={{fontWeight: 'bold'}}>Text:</td>
                                <td>{selectedCashflow.Text || '-'}</td>
                            </tr>
                            <tr>
                                <td style={{fontWeight: 'bold'}}>Částka:</td>
                                <td>{formatter.format(selectedCashflow.PriceVat)} {selectedCashflow.currencyCode}</td>
                            </tr>
                            <tr>
                                <td style={{fontWeight: 'bold'}}>Zaplaceno:</td>
                                <td>{formatter.format(selectedCashflow.PaidAmount || 0)} {selectedCashflow.currencyCode}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Note */}
                <div style={{marginBottom: 24}}>
                    <Label style={{display: 'block', marginBottom: 8, fontWeight: 'bold'}}>
                        Poznámka (volitelné):
                    </Label>
                    <TextArea
                        value={note}
                        onChange={(e) => setNote(e.value)}
                        rows={3}
                        style={{width: '100%'}}
                        placeholder="Důvod manuálního párování..."
                    />
                </div>

                {/* Actions */}
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
                    <Button
                        themeColor="base"
                        onClick={onClose}
                    >
                        Zrušit
                    </Button>
                    <Button
                        themeColor="primary"
                        onClick={handleSubmit}
                        disabled={!selectedCashflow || isLoading}
                    >
                        {isLoading ? 'Ukládám...' : 'Spárovat'}
                    </Button>
                </div>
            </div>
        </Window>
    );
};

export default MatchDialog;
